# Nut-Free Dietary Guidance

**CORE PRINCIPLE:** Eliminate all tree nuts and peanuts — including nut-derived oils, flours, butters, and extracts — while maintaining texture, richness, and protein through seed, grain, and legume-based alternatives. Treat this as an allergy-level restriction, not a preference.

## CRITICAL SAFETY RULES

### Severity Awareness
**PRINCIPLE:** Nut allergies can cause anaphylaxis; cross-contamination guidance matters as much as ingredient substitution

- Never suggest "traces are fine" or "a little won't hurt"
- When suggesting processed ingredients (pesto, cereals, chocolate, granola), specify choosing brands labelled nut-free
- Flag high-risk hidden sources prominently in recipe notes
- Prefer whole ingredients the cook can verify over processed blends

**HIGH-RISK INGREDIENTS TO INTERROGATE:**
- Pesto (traditionally pine nuts or cashews)
- Nut milks and nut flours in baking
- Satay sauces and marzipan (peanut/almond)
- Praline, nougat, frangipane
- Many curries (cashew or almond thickeners)
- Grand Marnier and some liqueurs (almond-based flavourings)
- Moru/kofta and korma sauces (cashew paste)
- Veggie burgers and meat substitutes (nut binders)
- Coconut decision: see section below

## THE COCONUT QUESTION

### Position on Coconut
**PRINCIPLE:** Botanically coconut is a fruit (drupe); most nut-allergy sufferers tolerate it, but regulation varies

- The EU and UK classify coconut as a tree nut for labelling; the US FDA historically did too (though guidance is evolving)
- Default to including coconut ONLY with an explicit note that coconut allergy is separate and rare, and the cook should confirm with their allergist or the recipe's intended eater
- If the request context suggests severe allergy, prefer omitting coconut and using alternatives below

## TEXTURE AND CRUNCH REPLACEMENTS

### Replacing Nuts for Texture
**PRINCIPLE:** Match the physical role — crunch, bulk, or toastiness — before worrying about flavour

**CRUNCH ALTERNATIVES:**
- Toasted sunflower seeds (closest all-purpose swap)
- Toasted pumpkin seeds (pepitas) — larger, greener, great in salads
- Toasted hemp hearts (smaller, softer crunch)
- Crispy chickpeas (roasted with oil and salt)
- Crispy fried shallots or garlic
- Toasted panko breadcrumbs (contains gluten — pair with GF requirement when combined)

**IN BAKING (nuts as mix-ins):**
- Chocolate chips, dried fruit, or freeze-dried berries
- Toasted sunflower seed kernels (1:1 for walnuts/pecans in cookies, scones, brownies)
- Oat crumble or streusel topping instead of nut streusel
- Puffed rice or millet for cluster/bark textures

**NUT FLOUR / GROUND ALMOND SUBSTITUTES:**
- Fine oat flour (adds chew, more binding)
- Sunflower seed flour (closest colour/mouthfeel; can turn green with baking soda — harmless, note it)
- Toasted sesame flour (strong flavour, best in savoury)
- All-purpose or GF blend + slightly more fat (nut flours are fatty; add butter/oil ~1 tbsp per 100g flour replaced)

## CREAMINESS AND RICHNESS REPLACEMENTS

### Nut-Free Creaminess
**PRINCIPLE:** Cashew and almond creams are the standard dairy-free route — replace them with non-nut bases

**CREAMY SAUCE BASES:**
- Silken tofu blended with stock or plant milk (neutral, protein-rich)
- Sunflower seed butter or tahini thinned with water and lemon (tahini for savoury applications)
- White bean puree (butterbeans/cannellini) with olive oil and garlic — excellent in pasta sauces
- Steamed and blended cauliflower or potato for low-fat creaminess
- Coconut cream ONLY with the coconut caveat noted above

**NUT BUTTER SWAPS (per 100g):**
- Peanut/almond butter → sunflower seed butter (sunbutter) 1:1, or tahini (more bitter), or pumpkin seed butter
- Cashew butter in sauces → white bean puree + tahini + lemon
- Nut butter in energy balls → sunbutter or date paste + oats

## FLAVOUR REPLACEMENTS

### Where Nuts Carry the Flavour
**PRINCIPLE:** Identify what the nut contributes — fat, sweetness, roastiness, or bitterness — and rebuild it

- **Roasted depth (e.g. missing toasted hazelnut)** → toasted sesame oil (small amounts), brown butter (if dairy OK), roasted chickpea flour
- **Rich fat (e.g. macadamia in cookies)** → extra butter/coconut oil/vegan butter plus a textural swap from the crunch list
- **Peanut sauce profile** → soy sauce + tahini or sunbutter + lime + a little brown sugar + optional chilli
- **Praline/caramel nut element** → caramelised crispy quinoa or toasted sunflower seeds in caramel
- **Almond extract (frangipane, marzipan)** → imitation almond extract is usually synthetic (safe for most nut allergies but confirm brand); otherwise lean into vanilla + cherry (cherry pairs with the flavour memory)

## COMMON DISH ADAPTATIONS

### Typical Nut-Heavy Dishes
**PRINCIPLE:** Rework the dish's structure rather than apologising for a missing ingredient

- **Korma/curries** → blend sauce base from sautéed onion, garlic, ginger, and yoghurt or silken tofu instead of cashew paste
- **Pesto** → basil + parmesan (or nutritional yeast) + olive oil + toasted sunflower seeds; or basil + sun-dried tomato
- **Waldorf salad** → apple, celery, grapes, and toasted sunflower seeds in a yoghurt or mayo dressing
- **Baklava** → layered filo with toasted seed filling (sunflower + sesame + honey)
- **Satay** → grilled skewers with a soy-tahini-lime sauce
- **Macarons/frangipane desserts** → do NOT attempt 1:1; suggest alternative desserts honestly
- **Nut-crusted fish/chicken** → crust with toasted panko, sesame seeds, or crushed crispy rice cereal + herbs

## AVOID ENTIRELY

- All tree nuts: almond, cashew, walnut, pecan, pistachio, hazelnut, macadamia, brazil, pine nuts, chestnut
- Peanuts (a legume, but grouped with nut allergies)
- Nut flours, nut meals, nut milks, nut butters, nut oils (peanut oil in restaurants is a classic hidden danger)
- Marzipan, frangipane, praline, nougat, gianduja, Nutella-type spreads
- Most pestos unless verified
- Cantonese/Szechuan dishes cooked in peanut oil
- Grand Marnier, amaretto, Frangelico, orgeat syrup (almond)
- Mole sauces (usually contain nuts or seeds — verify)
- Cross-contamination risk: loose bakery items, bulk-bin items, shared-toaster granolas

## NOTE FOR RECIPE OUTPUT

When generating a Nut-Free recipe, add a brief note in the recipe notes reminding the cook to check labels on any processed ingredient (sauces, chocolate, cereals) for "may contain nuts" warnings, and to confirm the coconut position with the person eating if coconut is used.

This guidance ensures nut-free recipes are safe by default and satisfying in texture and flavour, not merely nut-flavoured dishes with the joy removed.
